/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// core components
import { useTranslation } from 'react-i18next';
import * as actions from '../actionCreators';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import { getOfferBankAccounts, getBankAccountsIsLoaded, getBankAccountTypes } from '../selectors';
import { getCurrencies } from 'selectors/dictionariesSelectors';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ibanLengths } from '../ibanLengths';
import OfferPartnerBankAccountComponent from './OfferPartnerBankAccountComponent';

import { shape, required, items, concatFailure, Validator, Success, Failure, ValidationError } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { groupBy } from '../../../../../../../src/utils/functions';

const OfferPartnerBankAccountContainer = ({
  currencies,
  bankAccountTypes,
  partnerId,
  bankAccounts,
  bankAccountsLoaded,
  actions,
  documentId,
  siteId,
  clientBankaccountWrite
}) => {
  const [localBankAccounts, setLocalBankAccountList] = useState(bankAccounts);
  const [cardExpanded, setExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const ibanLengthIsValid = Validator(function ibanLengthIsValid(content) {
    if (content?.length < 2) return Failure(ValidationError(t('Validations.Generic.IBAN')));
    return ibanLengths[content?.substring(0, 2)] === content?.length
      ? Success
      : Failure(ValidationError(t('Validations.Generic.IBANLength', { value: ibanLengths[content?.substring(0, 2)] })));
  });

  const validatorBankSelection = items(
    shape({
      bankId: required,
      bankAccount: [ibanLengthIsValid] |> concatFailure,
      bankAccountType: required,
      currencyId: required
    })
  );

  const [validationBankSelection, validateBankSelection] = useDirtyFieldValidation(validatorBankSelection, dirtyInfo);
  const [validDefaultBankAccounts, setValidDefaultBankAccounts] = useState(true);

  const onExpand = (_, expanded) => {
    setExpanded(!expanded);
  };

  const addBankAccount = () => {
    let bankAccountsList = [...localBankAccounts, {}];
    setLocalBankAccountList(bankAccountsList);
    setSaveDisabled(false);
  };

  const getDefaultBankAccounts = localBankAccounts => {
    return localBankAccounts.filter(bank => bank.defaultAccount === true);
  };

  const defaultBankAccountsAreValid = defaultBankAccounts => {
    const result = groupBy(defaultBankAccounts, function (item) {
      return [item.defaultAccount, item.currencyId];
    });
    return !result.some(function (bankGroup) {
      return bankGroup.length > 1;
    });
  };

  const saveBankAccounts = (partnerId, localBankAccounts) => {
    if (!validateBankSelection(localBankAccounts)) return;

    const defaultBankAccounts = getDefaultBankAccounts(localBankAccounts);
    if (defaultBankAccounts.length > 1 && !defaultBankAccountsAreValid(defaultBankAccounts)) {
      setValidDefaultBankAccounts(false);
    } else {
      setSaving(true);
      actions.saveBankAccounts(documentId, siteId, partnerId, localBankAccounts).then(() => setSaving(false));
      setSaveDisabled(true);
    }
  };

  useEffect(() => {
    validateBankSelection(localBankAccounts, dirtyInfo);
  }, [localBankAccounts, dirtyInfo]);

  const deleteBankAccount = idx => {
    let newBankAccountList = localBankAccounts.filter(x => localBankAccounts.indexOf(x) !== idx);
    setLocalBankAccountList(newBankAccountList);
    setSaveDisabled(false);
  };

  useEffect(() => {
    if (!bankAccountsLoaded) {
      actions.loadBankAccountTypes();
      actions.loadCurrencies();
      if (partnerId !== null) actions.loadOfferPartnerBankAccounts(partnerId, documentId, siteId);
    }
  }, [cardExpanded]);

  useEffect(() => {
    if (bankAccountsLoaded) {
      setLocalBankAccountList(bankAccounts);
    }
  }, [bankAccountsLoaded]);

  const loadBanks = search => {
    if (!search) {
      return Promise.resolve([]);
    }

    return actions.loadBanks(search);
  };

  const onBankAccountPropertyChange = idx => (prop1, prop2) => value => {
    let newlocalBankAccounts = Object.assign([], localBankAccounts);
    let value2 = value?.['bankName'];
    setDirtyInfo(`${prop1}`);
    if (prop1 === 'currencyId') {
      value2 = currencies.filter(x => x.currencyId === value)[0]['code'];
      prop2 = 'currencyCode';
    }

    if (prop1 === 'defaultAccount' && value === true) {
      let currentBankAccountCurrencyId = localBankAccounts[idx].currencyId;
      newlocalBankAccounts = localBankAccounts.map(el =>
        el.currencyId === currentBankAccountCurrencyId ? Object.assign({}, el, { [prop1]: false }) : Object.assign({}, el)
      );
    }

    setLocalBankAccountList(
      Object.assign([], {
        ...newlocalBankAccounts,
        [idx]: {
          ...newlocalBankAccounts[idx],
          [prop1]: value?.['bankId'] ?? value,
          ...(prop2 && { [prop2]: value2?.['bankName'] ?? value2 })
        }
      })
    );
    setSaveDisabled(false);
    setValidDefaultBankAccounts(true);
  };

  return (
    <OfferPartnerBankAccountComponent
      currencies={currencies}
      bankAccountTypes={bankAccountTypes}
      bankAccounts={localBankAccounts}
      actions={actions}
      onPropertyChange={onBankAccountPropertyChange}
      loadBanks={loadBanks}
      onExpand={onExpand}
      addBankAccount={addBankAccount}
      partnerId={partnerId}
      localBankAccounts={localBankAccounts}
      saveBankAccounts={saveBankAccounts}
      deleteBankAccount={deleteBankAccount}
      cardExpanded={cardExpanded}
      saveDisabled={saveDisabled}
      validationBankSelection={validationBankSelection}
      clientBankaccountWrite={clientBankaccountWrite}
      validDefaultBankAccounts={validDefaultBankAccounts}
      saving={saving}
    />
  );
};
OfferPartnerBankAccountContainer.propTypes = {
  currencies: PropTypes.array.isRequired,
  bankAccountTypes: PropTypes.array.isRequired,
  bankAccounts: PropTypes.array,
  bankAccountsLoaded: PropTypes.bool.isRequired,
  bankAccountsLoading: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  setIsDirty: PropTypes.func,
  clientBankaccountWrite: PropTypes.bool.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

function mapStateToProps(state, ownProps) {
  const currencies = getCurrencies(state);
  const bankAccountTypes = getBankAccountTypes(state);
  const bankAccounts = getOfferBankAccounts(state, ownProps.partnerId);
  const bankAccountsLoaded = getBankAccountsIsLoaded(state, ownProps.partnerId);
  const bankAccountsLoading = !bankAccountsLoaded;

  return {
    currencies,
    bankAccountTypes,
    bankAccounts,
    bankAccountsLoaded,
    bankAccountsLoading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions, ...dictionariesActions }, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OfferPartnerBankAccountContainer);
